import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  PhoneIncoming, PhoneOff, Clock, Brain, ArrowUpRight, ArrowDownRight,
  Sun, Moon, Building2, Voicemail, PhoneForwarded, Radio, RefreshCw, Database,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from 'recharts';
import { vdAnalyticsApi } from '../api';

/* ── Theme ─────────────────────────────────────────────────────── */
const ThemeCtx = createContext({ dark: true });
function useTheme() { return useContext(ThemeCtx); }

const palettes = {
  dark: {
    bg: '#0f1117', surface: '#141721', border: 'rgba(255,255,255,0.08)',
    text: '#ffffff', textSec: '#94a3b8', textMut: '#475569',
    accent: '#34d399', accentBg: 'rgba(52,211,153,0.12)',
    red: '#f87171', redBg: 'rgba(248,113,113,0.12)',
    blue: '#38bdf8', blueBg: 'rgba(56,189,248,0.12)',
    purple: '#818cf8', purpleBg: 'rgba(129,140,248,0.12)',
    amber: '#fbbf24', amberBg: 'rgba(251,191,36,0.12)',
    chart: ['#34d399', '#38bdf8', '#818cf8', '#f472b6', '#fbbf24', '#fb923c', '#a78bfa'],
    grid: 'rgba(255,255,255,0.06)', hover: 'rgba(255,255,255,0.03)',
  },
  light: {
    bg: '#f8fafc', surface: '#ffffff', border: 'rgba(0,0,0,0.08)',
    text: '#0f172a', textSec: '#475569', textMut: '#94a3b8',
    accent: '#059669', accentBg: 'rgba(5,150,105,0.1)',
    red: '#dc2626', redBg: 'rgba(220,38,38,0.08)',
    blue: '#0284c7', blueBg: 'rgba(2,132,199,0.08)',
    purple: '#4f46e5', purpleBg: 'rgba(79,70,229,0.08)',
    amber: '#d97706', amberBg: 'rgba(217,119,6,0.08)',
    chart: ['#059669', '#0284c7', '#4f46e5', '#db2777', '#d97706', '#ea580c', '#7c3aed'],
    grid: 'rgba(0,0,0,0.06)', hover: 'rgba(0,0,0,0.02)',
  },
};

function t(isDark) { return isDark ? palettes.dark : palettes.light; }

/* ── Helpers ───────────────────────────────────────────────────── */
function fmtDuration(sec) {
  if (!sec) return '0:00';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/* ── KPI Card ──────────────────────────────────────────────────── */
function KPICard({ label, value, sub, icon: Icon, trend, trendVal, colorKey, isDark }) {
  const p = t(isDark);
  const colors = { accent: { i: p.accent, b: p.accentBg }, red: { i: p.red, b: p.redBg }, blue: { i: p.blue, b: p.blueBg }, purple: { i: p.purple, b: p.purpleBg }, amber: { i: p.amber, b: p.amberBg } };
  const c = colors[colorKey] || colors.accent;
  const up = trend === 'up';

  return (
    <div
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className="group rounded-lg p-5 transition-all duration-200 hover:-translate-y-[2px]"
      style={{ background: p.surface, border: `1px solid ${p.border}` }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: c.b }}>
          <Icon size={18} strokeWidth={1.5} style={{ color: c.i }} />
        </div>
        {trendVal != null && (
          <span className="flex items-center gap-0.5 text-xs font-semibold" style={{ color: up ? p.accent : p.red }}>
            {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {trendVal}
          </span>
        )}
      </div>
      <div className="text-3xl font-bold tracking-tight" style={{ color: p.text }}>{value}</div>
      <div className="text-xs font-semibold uppercase tracking-[0.15em] mt-1" style={{ color: p.textMut }}>{label}</div>
      {sub && <div className="text-xs mt-1" style={{ color: p.textSec }}>{sub}</div>}
    </div>
  );
}

/* ── Chart Tooltip ─────────────────────────────────────────────── */
function ChartTooltip({ active, payload, label, isDark }) {
  if (!active || !payload?.length) return null;
  const p = t(isDark);
  return (
    <div className="rounded-lg p-3 text-xs" style={{ background: p.surface, border: `1px solid ${p.border}`, color: p.text }}>
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((e, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: e.color }} />
          <span style={{ color: p.textSec }}>{e.name}:</span>
          <span className="font-medium">{e.value}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Volume Chart ──────────────────────────────────────────────── */
function VolumeChart({ data, isDark }) {
  const p = t(isDark);
  const dayLabels = { '01': 'Mon', '02': 'Tue', '03': 'Wed', '04': 'Thu', '05': 'Fri', '06': 'Sat', '07': 'Sun' };
  const chartData = data.map(d => ({
    ...d,
    label: d.day?.slice(5) || d.day,
  }));

  return (
    <div data-testid="volume-chart" className="rounded-lg p-5 h-full" style={{ background: p.surface, border: `1px solid ${p.border}` }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold" style={{ color: p.text }}>Call Volume</h2>
          <p className="text-xs mt-0.5" style={{ color: p.textMut }}>Trend over period</p>
        </div>
        <div className="flex items-center gap-4 text-xs" style={{ color: p.textSec }}>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: p.accent }} />AI</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: p.blue }} />Transferred</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: p.red }} />Missed</span>
        </div>
      </div>
      {data.length === 0 ? (
        <div className="h-[220px] flex items-center justify-center text-sm" style={{ color: p.textMut }}>No data for this period</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gAI" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={p.accent} stopOpacity={0.3} />
                <stop offset="95%" stopColor={p.accent} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gTx" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={p.blue} stopOpacity={0.2} />
                <stop offset="95%" stopColor={p.blue} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 4" stroke={p.grid} vertical={false} />
            <XAxis dataKey="label" tick={{ fill: p.textMut, fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: p.textMut, fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip isDark={isDark} />} />
            <Area type="monotone" dataKey="ai" name="AI Handled" stroke={p.accent} fill="url(#gAI)" strokeWidth={2} dot={false} />
            <Area type="monotone" dataKey="transferred" name="Transferred" stroke={p.blue} fill="url(#gTx)" strokeWidth={2} dot={false} />
            <Area type="monotone" dataKey="missed" name="Missed" stroke={p.red} fill="transparent" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

/* ── Department Chart ──────────────────────────────────────────── */
function DeptChart({ data, isDark }) {
  const p = t(isDark);
  return (
    <div data-testid="dept-chart" className="rounded-lg p-5 h-full" style={{ background: p.surface, border: `1px solid ${p.border}` }}>
      <h2 className="text-base font-semibold mb-4" style={{ color: p.text }}>By Department</h2>
      {data.length === 0 ? (
        <div className="text-sm" style={{ color: p.textMut }}>No data</div>
      ) : (
        <div className="space-y-3">
          {data.map((d, i) => (
            <div key={d.name}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium" style={{ color: p.textSec }}>{d.name}</span>
                <span className="text-xs font-semibold" style={{ color: p.text }}>{d.calls}</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${d.pct}%`, background: p.chart[i % p.chart.length] }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Peak Hours ────────────────────────────────────────────────── */
function PeakHoursChart({ data, isDark }) {
  const p = t(isDark);
  const max = Math.max(...data.map(h => h.calls), 1);
  return (
    <div data-testid="peak-hours-chart" className="rounded-lg p-5" style={{ background: p.surface, border: `1px solid ${p.border}` }}>
      <h2 className="text-base font-semibold mb-1" style={{ color: p.text }}>Peak Hours</h2>
      <p className="text-xs mb-4" style={{ color: p.textMut }}>Call distribution by hour</p>
      {data.length === 0 ? (
        <div className="h-[160px] flex items-center justify-center text-sm" style={{ color: p.textMut }}>No data</div>
      ) : (
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="4 4" stroke={p.grid} vertical={false} />
            <XAxis dataKey="hour" tick={{ fill: p.textMut, fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: p.textMut, fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip isDark={isDark} />} />
            <Bar dataKey="calls" name="Calls" radius={[3, 3, 0, 0]}>
              {data.map((h, i) => {
                const intensity = h.calls / max;
                const color = intensity > 0.8 ? p.accent : intensity > 0.5 ? p.blue : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)';
                return <Cell key={i} fill={color} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

/* ── Live Calls Widget (SSE with polling fallback) ─────────────── */
function LiveCallsWidget({ isDark, onNewCall }) {
  const p = t(isDark);
  const [liveCalls, setLiveCalls] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initial load via API
  const fetchLive = useCallback(() => {
    vdAnalyticsApi.live()
      .then(data => { setLiveCalls(data.live_calls || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // SSE connection
  useEffect(() => {
    fetchLive();

    const url = vdAnalyticsApi.streamUrl();
    if (!url) return;

    const es = new EventSource(url);
    let mounted = true;

    es.addEventListener('connected', () => {
      if (mounted) setConnected(true);
    });

    es.onmessage = (event) => {
      if (!mounted) return;
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'new_call') {
          setLiveCalls(prev => [msg.data, ...prev].slice(0, 20));
          if (onNewCall) onNewCall(msg.data);
        }
      } catch {}
    };

    es.onerror = () => {
      if (mounted) setConnected(false);
    };

    // Fallback poll if SSE disconnects — check every 15s
    const fallback = setInterval(() => {
      if (!connected) fetchLive();
    }, 15000);

    return () => {
      mounted = false;
      es.close();
      clearInterval(fallback);
    };
  }, []);

  const dispColor = (d) => {
    if (d === 'completed') return p.accent;
    if (d === 'transferred') return p.blue;
    if (d === 'voicemail') return p.amber;
    return p.red;
  };

  return (
    <div data-testid="live-calls-widget" className="rounded-lg p-5" style={{ background: p.surface, border: `1px solid ${p.border}` }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Radio size={16} strokeWidth={1.5} style={{ color: connected ? p.accent : p.textMut }} />
            {connected && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full animate-ping" style={{ background: p.accent }} />}
          </div>
          <h2 className="text-base font-semibold" style={{ color: p.text }}>Live Activity</h2>
          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{
            background: connected ? p.accentBg : p.redBg,
            color: connected ? p.accent : p.red,
          }}>
            {connected ? 'streaming' : 'polling'}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: p.accentBg, color: p.accent }}>
            {liveCalls.length} recent
          </span>
        </div>
        <button onClick={fetchLive} className="p-1.5 rounded-md transition-colors" style={{ color: p.textMut }} data-testid="refresh-live-btn">
          <RefreshCw size={14} strokeWidth={1.5} />
        </button>
      </div>
      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-10 rounded-md animate-pulse" style={{ background: p.hover }} />)}</div>
      ) : liveCalls.length === 0 ? (
        <div className="text-sm py-6 text-center" style={{ color: p.textMut }}>No recent calls</div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {liveCalls.slice(0, 8).map((call, i) => (
            <div key={call.id || call.call_sid || i} className="flex items-center gap-3 rounded-md px-3 py-2 transition-all duration-300" style={{ background: p.hover }}>
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dispColor(call.disposition) }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate" style={{ color: p.text }}>{call.caller}</div>
                <div className="text-xs flex items-center gap-2" style={{ color: p.textMut }}>
                  <span>{call.department}</span>
                  <span>·</span>
                  <span>{fmtDuration(call.duration_seconds)}</span>
                  {call.ai_handled && <Brain size={11} style={{ color: p.accent }} />}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <span className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ background: `${dispColor(call.disposition)}20`, color: dispColor(call.disposition) }}>
                  {call.disposition}
                </span>
                <div className="text-xs mt-0.5" style={{ color: p.textMut }}>{timeAgo(call.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Recent Calls Table ────────────────────────────────────────── */
function RecentCallsTable({ calls, isDark, disposition, setDisposition }) {
  const p = t(isDark);
  const filters = [
    { key: '', label: 'All' },
    { key: 'completed', label: 'Completed' },
    { key: 'missed', label: 'Missed' },
    { key: 'voicemail', label: 'Voicemail' },
    { key: 'failed', label: 'Failed' },
    { key: 'transferred', label: 'Transferred' },
  ];
  const dispColor = (d) => {
    if (d === 'completed') return { bg: p.accentBg, text: p.accent };
    if (d === 'transferred') return { bg: p.blueBg, text: p.blue };
    if (d === 'voicemail') return { bg: p.amberBg, text: p.amber };
    return { bg: p.redBg, text: p.red };
  };

  return (
    <div data-testid="recent-calls-table" className="rounded-lg overflow-hidden" style={{ background: p.surface, border: `1px solid ${p.border}` }}>
      <div className="p-5 pb-3 flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base font-semibold" style={{ color: p.text }}>Recent Calls</h2>
        <div className="flex gap-1">
          {filters.map(f => (
            <button
              key={f.key}
              data-testid={`filter-${f.key || 'all'}`}
              onClick={() => setDisposition(f.key)}
              className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
              style={{
                background: disposition === f.key ? (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)') : 'transparent',
                color: disposition === f.key ? p.text : p.textMut,
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: `1px solid ${p.border}` }}>
              {['Caller', 'Department', 'Duration', 'Status', 'AI', 'Time'].map(h => (
                <th key={h} className="px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wider" style={{ color: p.textMut }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calls.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-sm" style={{ color: p.textMut }}>No calls found</td></tr>
            ) : calls.map((row, i) => {
              const sc = dispColor(row.disposition);
              return (
                <tr key={i} className="transition-colors" style={{ borderBottom: `1px solid ${p.border}` }}
                  onMouseEnter={e => e.currentTarget.style.background = p.hover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td className="px-5 py-3 font-medium" style={{ color: p.text }}>{row.caller}</td>
                  <td className="px-5 py-3" style={{ color: p.textSec }}>
                    <span className="flex items-center gap-1.5"><Building2 size={13} strokeWidth={1.5} />{row.department}</span>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs" style={{ color: p.textSec }}>{fmtDuration(row.duration_seconds)}</td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium" style={{ background: sc.bg, color: sc.text }}>
                      {row.disposition === 'voicemail' && <Voicemail size={11} />}
                      {row.disposition === 'transferred' && <PhoneForwarded size={11} />}
                      {row.disposition}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {row.ai_handled ? (
                      <span className="flex items-center gap-1 text-xs font-medium" style={{ color: p.accent }}><Brain size={13} strokeWidth={1.5} />Resolved</span>
                    ) : (
                      <span className="text-xs" style={{ color: p.textMut }}>Human</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs" style={{ color: p.textMut }}>{timeAgo(row.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Main Dashboard ────────────────────────────────────────────── */
export default function VDAnalytics() {
  const [isDark, setIsDark] = useState(true);
  const [range, setRange] = useState('7d');
  const [disposition, setDisposition] = useState('');
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [summary, setSummary] = useState(null);
  const [volume, setVolume] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [calls, setCalls] = useState([]);
  const [hasData, setHasData] = useState(true);

  const p = t(isDark);
  const ranges = [
    { key: 'today', label: 'Today' },
    { key: '7d', label: '7 Days' },
    { key: '30d', label: '30 Days' },
  ];

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      vdAnalyticsApi.summary(range),
      vdAnalyticsApi.volume(range),
      vdAnalyticsApi.departments(range),
      vdAnalyticsApi.peakHours(range),
      vdAnalyticsApi.calls(range, 15, disposition),
    ]).then(([sum, vol, dept, hours, callsData]) => {
      setSummary(sum);
      setVolume(vol || []);
      setDepartments(dept || []);
      setPeakHours(hours || []);
      setCalls(callsData?.calls || []);
      setHasData((sum?.total || 0) > 0);
      setLoading(false);
    }).catch(() => {
      setSummary(null);
      setHasData(false);
      setLoading(false);
    });
  }, [range, disposition]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSeed = () => {
    setSeeding(true);
    vdAnalyticsApi.seed().then(() => {
      setSeeding(false);
      loadData();
    }).catch(() => setSeeding(false));
  };

  return (
    <ThemeCtx.Provider value={{ dark: isDark }}>
      <div
        data-testid="vd-analytics"
        className="space-y-5 max-w-7xl mx-auto rounded-xl p-6 transition-colors duration-300"
        style={{ background: p.bg, color: p.text, minHeight: 'calc(100vh - 120px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
            <p className="text-xs mt-1" style={{ color: p.textMut }}>IVR performance overview</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg overflow-hidden" style={{ border: `1px solid ${p.border}` }}>
              {ranges.map(r => (
                <button key={r.key} data-testid={`range-${r.key}`} onClick={() => setRange(r.key)}
                  className="px-3 py-1.5 text-xs font-medium transition-colors"
                  style={{ background: range === r.key ? (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)') : 'transparent', color: range === r.key ? p.text : p.textMut }}>
                  {r.label}
                </button>
              ))}
            </div>
            <button data-testid="theme-toggle-btn" onClick={() => setIsDark(v => !v)}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
              style={{ border: `1px solid ${p.border}`, color: p.textSec }}>
              {isDark ? <Sun size={15} strokeWidth={1.5} /> : <Moon size={15} strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {/* No data — seed prompt */}
        {!loading && !hasData && (
          <div className="rounded-lg p-8 text-center" style={{ background: p.surface, border: `1px solid ${p.border}` }}>
            <Database size={32} strokeWidth={1.5} className="mx-auto mb-3" style={{ color: p.textMut }} />
            <h3 className="text-lg font-semibold mb-1" style={{ color: p.text }}>No call data yet</h3>
            <p className="text-sm mb-4" style={{ color: p.textSec }}>Seed 30 days of realistic sample data to explore the dashboard.</p>
            <button data-testid="seed-data-btn" onClick={handleSeed} disabled={seeding}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ background: p.accent, color: isDark ? '#0f1117' : '#ffffff', opacity: seeding ? 0.6 : 1 }}>
              {seeding ? 'Seeding...' : 'Generate Sample Data'}
            </button>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-32 rounded-lg animate-pulse" style={{ background: p.surface }} />)}
          </div>
        )}

        {/* Dashboard content */}
        {!loading && hasData && summary && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <KPICard isDark={isDark} label="Total Calls" value={summary.total.toLocaleString()} sub={`${range === 'today' ? 'Today' : range === '7d' ? 'This week' : 'This month'}`} icon={PhoneIncoming} colorKey="accent" />
              <KPICard isDark={isDark} label="Avg Duration" value={fmtDuration(summary.avg_duration)} sub="Minutes" icon={Clock} colorKey="blue" />
              <KPICard isDark={isDark} label="Missed / Failed" value={summary.missed} sub={`${summary.total > 0 ? ((summary.missed / summary.total * 100).toFixed(1)) : 0}% rate`} icon={PhoneOff} colorKey="red" />
              <KPICard isDark={isDark} label="Voicemail" value={summary.voicemail || 0} sub="Messages left" icon={Voicemail} colorKey="amber" />
              <KPICard isDark={isDark} label="AI Resolution" value={`${summary.ai_rate}%`} sub={`${summary.ai_handled} of ${summary.total}`} icon={Brain} colorKey="purple" />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2"><VolumeChart data={volume} isDark={isDark} /></div>
              <DeptChart data={departments} isDark={isDark} />
            </div>

            {/* Live + Peak Hours */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <LiveCallsWidget isDark={isDark} onNewCall={() => {
                // Refresh KPIs and recent calls when a new call arrives via SSE
                vdAnalyticsApi.summary(range).then(s => { if (s) setSummary(s); });
                vdAnalyticsApi.calls(range, 15, disposition).then(c => { if (c?.calls) setCalls(c.calls); });
              }} />
              <PeakHoursChart data={peakHours} isDark={isDark} />
            </div>

            {/* Recent Calls Table */}
            <RecentCallsTable calls={calls} isDark={isDark} disposition={disposition} setDisposition={setDisposition} />
          </>
        )}
      </div>
    </ThemeCtx.Provider>
  );
}
